/** @file library.apply: copies standard clips of a library puppet onto the puppet by node name. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { unknownNodeWarnings } from '../clip/unknownNodeWarnings';
import { checkClip } from '../puppetDoc/checkClip';
import type { Command } from '../types/command';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { refuseAttachmentPrefix } from '../validate/refuseAttachmentPrefix';
import { renameClipNodes } from './renameClipNodes';

/**
 * `library.apply { library, clips?, nodeMap?, replace? }` → `{ clips, unknownNodes }`: each clip (default all) is
 * copied with a new id and `nodeMap` renames; nodes the puppet lacks are kept and warned (playback skips them). A
 * taken name needs `replace`. Inverse per clip: delete it, or re-import the replaced clip.
 */
export const libraryApplyCommand: CommandDef<'library.apply'> = {
  name: 'library.apply',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['library', 'clips', 'nodeMap', 'replace']);
    return compact({
      library: r.str('library'), clips: r.opt('clips', (k) => r.check(k, checkArray(checkString())) as string[]),
      nodeMap: r.opt('nodeMap', (k) => r.check(k, checkRecord(checkString())) as Record<string, string>), replace: r.opt('replace', r.bool),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const lib = ctx.libraries[input.library]
      ?? throwError(ERROR_CODES.NOT_FOUND, { library: input.library }, { path: '/input/library', hint: `libraries: ${Object.keys(ctx.libraries).join(', ')}` });
    const names = input.clips ?? Object.keys(lib.clips);
    const inverse: Command[] = [];
    const warnings: Warning[] = [];
    const unknownNodes: Record<string, string[]> = {};
    names.forEach((name, i) => {
      const src = lib.clips[name] ?? throwError(ERROR_CODES.NOT_FOUND, { clip: name }, { path: `/input/clips/${i}`, hint: `library clips: ${Object.keys(lib.clips).join(', ')}` });
      refuseAttachmentPrefix(name, `/input/clips/${i}`);
      const prev = draft.clips[name];
      if (prev !== undefined && input.replace !== true) {
        throwError(ERROR_CODES.VALIDATION, { clip: name }, { path: '/input/clips', hint: 'pass replace: true, or clip.rename / clip.delete the existing clip' });
      }
      inverse.push(prev === undefined ? { name: 'clip.delete', input: { clip: name } } : clipInverse(prev));
      const out = { ...renameClipNodes(src, input.nodeMap ?? {}), id: ctx.makeId('clp'), name };
      draft.clips[name] = out;
      checkClip(name, out);
      const w = unknownNodeWarnings(draft.puppet, name, out);
      warnings.push(...w);
      if (w.length > 0) unknownNodes[name] = w.map((x) => String(x.params?.node));
    });
    return { result: { clips: names, unknownNodes }, warnings, inverse: inverse.reverse() };
  },
};
